/**
 * Namens- und URL-Logik des `DynamicIcon`.
 *
 * Hier steht nur die reine Auflösung „Nutzername → Tabler-Modulname" bzw.
 * „Wert ist eine Bild-URL": Legacy-Font-Awesome-Namen, Kurz-Aliase,
 * Pascal-Case-Fallback. Framework-frei — kein React, kein JSX, kein
 * DOM-Zugriff beim Import, damit Frontends, Cockpits **und** Backends dieses
 * Paket nutzen können.
 *
 * Die Komponente und ihr Lademechanismus liegen in `@core/ui`: genau eine
 * `DynamicIcon`-Fassung für Kundenfrontends und Cockpits, die ihre Icons über
 * das virtuelle Modul des Vite-Plugins `tablerIcons()` (`@core/ui/vite`) lädt.
 * Beides braucht React bzw. einen Bundler und hat deshalb hier nichts zu
 * suchen; geteilt wird nur die Logik, nicht das Laden.
 */
/**
 * Abbildung alter Font-Awesome-Namen auf Tabler-Icon-Namen.
 * Sichert die Rückwärtskompatibilität mit bereits in der Datenbank
 * gespeicherten Icon-Werten.
 */
export const legacyNameMap = {
    faGraduationCap: 'IconSchool',
    faChartBar: 'IconChartBar',
    faFile: 'IconFile',
    faLeaf: 'IconLeaf',
    faMouse: 'IconMouse',
    faEuroSign: 'IconCurrencyEuro',
    faFutbol: 'IconBallFootball',
    faCheckCircle: 'IconCircleCheck',
    faHandHoldingUsd: 'IconHandHoldingDollar',
    faHeart: 'IconHeart',
    faBirthdayCake: 'IconCake',
    faCalendar: 'IconCalendar',
    faCalendarAlt: 'IconCalendarEvent',
    faMask: 'IconMask',
    faVolumeUp: 'IconVolume',
    faCoins: 'IconCoins',
    faBoxOpen: 'IconPackage',
    faFileAlt: 'IconFileDescription',
    faUser: 'IconUser',
    faUserFriends: 'IconUsers',
    faUsers: 'IconUsersGroup',
    faAward: 'IconAward',
    faProjectDiagram: 'IconHierarchy',
    faLightbulb: 'IconLamp',
    faRocket: 'IconRocket',
    faPiggyBank: 'IconPigMoney',
    faVoteYea: 'IconChecks',
    faTrophy: 'IconTrophy',
};
/**
 * Kurz-Aliase für häufig in Inhalten gepflegte Icon-Werte (klein geschrieben).
 * Sie greifen vor dem Pascal-Case-Fallback, weil der Fallback aus `drop`
 * `IconDrop` machen würde — gemeint ist aber `IconDroplet`.
 */
export const commonShortAliases = {
    leaf: 'IconLeaf',
    tree: 'IconTree',
    sun: 'IconSun',
    drop: 'IconDroplet',
    droplet: 'IconDroplet',
    water: 'IconDroplet',
    sprout: 'IconSeedling',
    seedling: 'IconSeedling',
    gift: 'IconGift',
    mountain: 'IconMountain',
    flower: 'IconFlower',
    recycle: 'IconRecycle',
    wind: 'IconWind',
};
/**
 * Löst einen beliebigen Icon-Wert auf den Tabler-Modulnamen auf.
 *
 * Reihenfolge: Legacy-Map → Kurz-Alias (klein geschrieben) → bereits
 * vollständiger `Icon…`-Name → Pascal-Case-Fallback (`chart-bar` → `IconChartBar`).
 *
 * @returns Der Tabler-Modulname oder `null`, wenn kein Wert übergeben wurde.
 */
export const normalizeIconName = (rawName) => {
    if (!rawName)
        return null;
    if (legacyNameMap[rawName])
        return legacyNameMap[rawName];
    const lower = String(rawName).toLowerCase();
    if (commonShortAliases[lower]) {
        return commonShortAliases[lower];
    }
    if (rawName.startsWith('Icon')) {
        return rawName;
    }
    // Aus "chart-bar" oder "user" wird "IconChartBar" bzw. "IconUser"
    const pascal = rawName
        .replace(/[-_]([a-z])/g, (_, g) => g.toUpperCase())
        .replace(/^[a-z]/, (g) => g.toUpperCase());
    return `Icon${pascal}`;
};
/**
 * Prüft, ob der Icon-Wert eine eigene Bild-URL (absolut oder wurzel-relativ)
 * statt eines Tabler-Icon-Namens ist.
 */
export const isCustomIconUrl = (value) => typeof value === 'string' && /^(https?:\/\/|\/)/i.test(value.trim());
//# sourceMappingURL=dynamicIcon.js.map